import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { invoicesAPI, purchasesAPI, settingsAPI, certificatesAPI } from '../../api';
import { clientsAPI } from '../../api';
import ClientPicker from '../../components/ClientPicker';
import Layout from '../../components/Layout';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { X, Eye, Building2, User, Pencil, Plus, Mail, Phone, MapPin, Printer, Info, Link, FileText, Download } from 'lucide-react';
import SlideOver from '../../components/ui/SlideOver';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { MinimalRichTextEditor } from '../../components/ui/MinimalRichTextEditor';
import { Card, CardContent } from '../../components/ui/Card';
import InvoicePaymentForm from '../../components/invoices/InvoicePaymentForm';
import PaymentHistoryModal from '../../components/invoices/PaymentHistoryModal';
import { getEffectiveInvoiceStatus } from '../../lib/invoiceStatus';

/** Settings → Invoice: `default_vat_rate` (0 when “VAT not applied” is checked). */
function resolveDefaultInvoiceVatRate(invoicePrefs) {
  const raw = invoicePrefs?.default_vat_rate;
  if (raw === 0) return 0;
  const n = parseFloat(raw);
  return Number.isFinite(n) && n >= 0 ? n : 20;
}

/** Add calendar days to a yyyy-MM-dd string using local dates (avoids UTC parsing shifts). */
function addDaysToIsoDate(ymd, daysToAdd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  const add = Number.isFinite(daysToAdd) ? Math.trunc(daysToAdd) : 0;
  const [y, m, d] = ymd.split('-').map((v) => parseInt(v, 10));
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return '';
  dt.setDate(dt.getDate() + add);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function parseClientPaymentTermsDays(client) {
  if (!client) return null;
  const raw = client.paymentTermsDays ?? client.payment_terms_days;
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Client-specific terms when present; otherwise tenant invoice default payment due days (Settings). */
function getEffectiveInvoicePaymentTermsDays(client, tenantDefaultDays) {
  const fromClient = parseClientPaymentTermsDays(client);
  if (fromClient !== null) return fromClient;
  const d = parseInt(tenantDefaultDays, 10);
  return Number.isFinite(d) && d >= 0 ? d : 30;
}

export default function InvoiceEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const legacySendIntent = searchParams.get('send');
  const isEditMode = !!id;
  const formRef = useRef(null);
  const { registerCheck, unregisterCheck } = useUnsavedChanges();
  const hasUnsavedChangesRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const fetchingRef = useRef(false);

  // Invoice form state
  const [formData, setFormData] = useState({
    clientId: '',
    invoiceNum: '',
    reference: '',
    dateCreated: new Date().toISOString().split('T')[0],
    dateIssued: '',
    dueDate: '',
    vatRate: 20,
    notes: '',
    terms: '',
    privateNotes: '',
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [showNewClientSlideOver, setShowNewClientSlideOver] = useState(false);
  const [showLabourInfoSlideOver, setShowLabourInfoSlideOver] = useState(false);
  const [showPriceLookupSlideOver, setShowPriceLookupSlideOver] = useState(false);
  const [activeInfoTab, setActiveInfoTab] = useState('notes'); // 'notes' | 'terms' | 'privateNotes'
  
  // Client search/combobox state (for create mode)
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [clientSearchSelectedIndex, setClientSearchSelectedIndex] = useState(-1);
  const [showClientSearchDropdown, setShowClientSearchDropdown] = useState(false);
  const clientSearchTimeoutRef = useRef(null);
  const clientSearchContainerRef = useRef(null);
  const [materials, setMaterials] = useState([]);
  const [labour, setLabour] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [invoiceAmountPaid, setInvoiceAmountPaid] = useState(0);
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [invoiceNumError, setInvoiceNumError] = useState('');
  const [checkingInvoiceNum, setCheckingInvoiceNum] = useState(false);
  const [isEditingInvoiceNum, setIsEditingInvoiceNum] = useState(false);
  const [profitCalcEnabled, setProfitCalcEnabled] = useState(false);
  
  // Certificate linking state
  const [linkedCertificates, setLinkedCertificates] = useState([]);
  const [showCertificateLinkModal, setShowCertificateLinkModal] = useState(false);
  const [availableCertificates, setAvailableCertificates] = useState([]);
  const [certificatesLoading, setCertificatesLoading] = useState(false);
  const [linkingCertificate, setLinkingCertificate] = useState(false);

  // Track initial form state for unsaved changes detection
  const initialFormStateRef = useRef(null);
  const initialMaterialsRef = useRef(null);
  const initialLabourRef = useRef(null);
  const initialClientRef = useRef(null);
  const vatRateInputRef = useRef(null);

  // Material autocomplete state per material row
  const [materialAutocomplete, setMaterialAutocomplete] = useState({});
  const [materialSuggestCoords, setMaterialSuggestCoords] = useState(null);
  const materialDescInputRefs = useRef({});
  const autocompleteTimeoutRef = useRef({});

  // Default values from settings
  const [defaultInvoiceNotes, setDefaultInvoiceNotes] = useState('');
  const [defaultInvoiceTerms, setDefaultInvoiceTerms] = useState('');
  const [defaultPaymentDueDays, setDefaultPaymentDueDays] = useState(30);
  
  // Helper function to convert ISO date string to yyyy-MM-dd format for date inputs
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // If it's already in yyyy-MM-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    // If it's an ISO datetime string, extract just the date part
    if (dateString.includes('T')) return dateString.split('T')[0];
    // Otherwise try to parse and format
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  // Helper function to get status badge classes
  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
      sent: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
      'part-paid': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
      'paid-in-full': 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      overdue: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  };

  // Helper function to format status for display
  const formatStatus = (status) => {
    if (!status) return '';
    return status.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const fetchInvoice = useCallback(async () => {
    if (!id || fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      setLoading(true);
      setError('');
      setSaveSuccess(false);
      const [invoice, settingsPayload] = await Promise.all([
        invoicesAPI.getOne(id),
        settingsAPI.get().catch(() => ({ settings: { preferences: {} } })),
      ]);
      const tenantDefaultVat = resolveDefaultInvoiceVatRate(
        settingsPayload?.settings?.preferences?.invoice
      );

      const rawVatRate = invoice.vatRate;
      const mappedVatRate = rawVatRate === null || rawVatRate === undefined || Number.isNaN(parseFloat(rawVatRate))
        ? tenantDefaultVat
        : parseFloat(rawVatRate);

      setFormData({
        clientId: invoice.client.id,
        invoiceNum: invoice.invoiceNum,
        reference: invoice.reference || '',
        dateCreated: formatDateForInput(invoice.dateCreated) || new Date().toISOString().split('T')[0],
        dateIssued: formatDateForInput(invoice.dateIssued) || '',
        dueDate: formatDateForInput(invoice.dueDate) || '',
        vatRate: mappedVatRate,
        notes: invoice.notes || '',
        terms: invoice.terms || '',
        privateNotes: invoice.privateNotes || '',
      });

      setSelectedClient(invoice.client);
      setInvoiceStatus(invoice.status || 'draft');
      setInvoiceAmountPaid(invoice.amountPaid || 0);
      setMaterials((invoice.materials || []).map(m => ({
        ...m,
        costPrice: m.costPrice || 0,
        markup: m.markup || 0,
      })));
      setLabour(invoice.labour || []);
      setLinkedCertificates(invoice.certificates || []);
      // Reset invoice number editing state when loading invoice
      setIsEditingInvoiceNum(false);

      // Store initial state for comparison
      initialFormStateRef.current = {
        clientId: invoice.client.id,
        invoiceNum: invoice.invoiceNum,
        reference: invoice.reference || '',
        dateCreated: formatDateForInput(invoice.dateCreated) || new Date().toISOString().split('T')[0],
        dateIssued: formatDateForInput(invoice.dateIssued) || '',
        dueDate: formatDateForInput(invoice.dueDate) || '',
        vatRate: mappedVatRate,
        notes: invoice.notes || '',
        terms: invoice.terms || '',
        privateNotes: invoice.privateNotes || '',
      };
      initialMaterialsRef.current = JSON.parse(JSON.stringify(invoice.materials || []));
      initialLabourRef.current = JSON.parse(JSON.stringify(invoice.labour || []));
      initialClientRef.current = invoice.client;
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError(err.message || 'Failed to load invoice');
      }
      console.error('Error fetching invoice:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [id]);

  useEffect(() => {
    let isMounted = true;

    // Reset client search state when switching modes
    if (!isEditMode) {
      setClientSearchQuery('');
      setClientSearchResults([]);
      setShowClientSearchDropdown(false);
      setClientSearchSelectedIndex(-1);
    }

    // Load settings for both edit and create modes (for the "Load default" links)
    const loadSettings = async () => {
      try {
        const settingsResponse = await settingsAPI.get();
        const defaultTerms = settingsResponse?.settings?.preferences?.invoice?.default_invoice_terms || '';
        const defaultNotes = settingsResponse?.settings?.preferences?.invoice?.default_invoice_notes || '';
        const defaultDueDays = settingsResponse?.settings?.preferences?.invoice?.default_payment_due_days || 30;
        if (isMounted) {
          setDefaultInvoiceTerms(defaultTerms);
          setDefaultInvoiceNotes(defaultNotes);
          setDefaultPaymentDueDays(defaultDueDays);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };
    
    loadSettings();

    if (isEditMode && id && !fetchingRef.current) {
      fetchInvoice();
    } else if (!isEditMode) {
      // Same <InvoiceEditor /> instance is reused for /new vs /:id — clear edit-only state immediately
      setSelectedClient(null);
      setInvoiceStatus(null);
      setInvoiceAmountPaid(0);
      setLinkedCertificates([]);
      setIsEditingInvoiceNum(false);
      setSaveSuccess(false);
      setError('');

      // Fetch next invoice number from API and load default settings
      const fetchNextInvoiceNumber = async () => {
        try {
          // Fetch both invoice number and settings in parallel
          const [invoiceNumberResponse, settingsResponse] = await Promise.all([
            invoicesAPI.getNextNumber(),
            settingsAPI.get().catch(() => ({ settings: { preferences: {} } })), // Gracefully handle settings fetch failure
          ]);
          
          console.log('Next invoice number API response:', invoiceNumberResponse);
          
          // Get default values from settings
          const defaultTerms = settingsResponse?.settings?.preferences?.invoice?.default_invoice_terms || '';
          const defaultNotes = settingsResponse?.settings?.preferences?.invoice?.default_invoice_notes || '';
          const defaultVat = resolveDefaultInvoiceVatRate(settingsResponse?.settings?.preferences?.invoice);
          
          // Store defaults in state for use by the "Load default" links
          if (isMounted) {
            setDefaultInvoiceTerms(defaultTerms);
            setDefaultInvoiceNotes(defaultNotes);
          }
          
          if (isMounted && invoiceNumberResponse && invoiceNumberResponse.invoiceNum) {
            const today = new Date().toISOString().split('T')[0];
            const newFormData = {
              clientId: '',
              invoiceNum: invoiceNumberResponse.invoiceNum,
              reference: '',
              dateCreated: today,
              dateIssued: '',
              dueDate: '',
              vatRate: defaultVat,
              notes: '',
              terms: defaultTerms,
              privateNotes: '',
            };
            setFormData(newFormData);
            // Store initial state for new invoice (empty state)
            initialFormStateRef.current = {
              clientId: '',
              invoiceNum: newFormData.invoiceNum,
              reference: '',
              dateCreated: newFormData.dateCreated,
              dateIssued: '',
              dueDate: '',
              vatRate: defaultVat,
              notes: '',
              terms: defaultTerms,
              privateNotes: '',
            };
            // Initialize with one blank row for new invoices
            const blankMaterial = {
              id: `temp-${Date.now()}`,
              description: '',
              quantity: 1,
              unitPrice: 0,
              partNumber: '',
              notes: '',
              costPrice: 0,
              markup: 0,
            };
            const blankLabour = {
              id: `temp-${Date.now() + 1}`,
              description: '',
              hours: 1,
              hourlyRate: 0,
              notes: '',
            };
            setMaterials([blankMaterial]);
            setLabour([blankLabour]);
            initialMaterialsRef.current = [blankMaterial];
            initialLabourRef.current = [blankLabour];
            initialClientRef.current = null;
          }
        } catch (err) {
          console.error('Error fetching next invoice number:', err);
          // Log the full error for debugging
          console.error('Full error details:', {
            message: err.message,
            response: err.response?.data,
            status: err.response?.status,
          });
          
          // Try to load settings even if invoice number fetch fails
          let defaultTerms = '';
          let defaultNotes = '';
          let defaultVat = 20;
          try {
            const settingsResponse = await settingsAPI.get();
            defaultTerms = settingsResponse?.settings?.preferences?.invoice?.default_invoice_terms || '';
            defaultNotes = settingsResponse?.settings?.preferences?.invoice?.default_invoice_notes || '';
            defaultVat = resolveDefaultInvoiceVatRate(settingsResponse?.settings?.preferences?.invoice);
          } catch (settingsErr) {
            console.error('Error fetching settings:', settingsErr);
          }
          
          // Store defaults in state
          if (isMounted) {
            setDefaultInvoiceTerms(defaultTerms);
            setDefaultInvoiceNotes(defaultNotes);
          }
          
          // Fallback to timestamp-based number if API fails
          if (isMounted) {
            const timestamp = Date.now().toString().slice(-6);
            const today = new Date().toISOString().split('T')[0];
            const newFormData = {
              clientId: '',
              invoiceNum: `INV-${timestamp}`,
              reference: '',
              dateCreated: today,
              dateIssued: '',
              dueDate: '',
              vatRate: defaultVat,
              notes: '',
              terms: defaultTerms,
              privateNotes: '',
            };
            setFormData(newFormData);
            initialFormStateRef.current = {
              clientId: '',
              invoiceNum: newFormData.invoiceNum,
              reference: '',
              dateCreated: newFormData.dateCreated,
              dateIssued: '',
              dueDate: '',
              vatRate: defaultVat,
              notes: '',
              terms: defaultTerms,
              privateNotes: '',
            };
            // Initialize with one blank row for new invoices
            const blankMaterial = {
              id: `temp-${Date.now()}`,
              description: '',
              quantity: 1,
              unitPrice: 0,
              partNumber: '',
              notes: '',
              costPrice: 0,
              markup: 0,
            };
            const blankLabour = {
              id: `temp-${Date.now() + 1}`,
              description: '',
              hours: 1,
              hourlyRate: 0,
              notes: '',
            };
            setMaterials([blankMaterial]);
            setLabour([blankLabour]);
            initialMaterialsRef.current = [blankMaterial];
            initialLabourRef.current = [blankLabour];
            initialClientRef.current = null;
          }
        }
      };

      fetchNextInvoiceNumber();
    }

    // Cleanup function to clear timeouts
    return () => {
      isMounted = false;
      fetchingRef.current = false;
      Object.keys(autocompleteTimeoutRef.current).forEach(key => {
        if (autocompleteTimeoutRef.current[key]) {
          clearTimeout(autocompleteTimeoutRef.current[key]);
        }
      });
    };
  }, [id, isEditMode, fetchInvoice]);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setFormData((prev) => {
      const next = { ...prev, clientId: client.id };
      if (prev.dateIssued) {
        const termsDays = getEffectiveInvoicePaymentTermsDays(client, defaultPaymentDueDays);
        next.dueDate = addDaysToIsoDate(prev.dateIssued, termsDays);
      }
      return next;
    });
    setShowClientPicker(false);
    // Reset search state
    setClientSearchQuery('');
    setClientSearchResults([]);
    setShowClientSearchDropdown(false);
    setClientSearchSelectedIndex(-1);
  };

  // Client search function (only triggers after 3+ characters)
  const searchClients = async (query) => {
    if (!query || query.trim().length < 3) {
      setClientSearchResults([]);
      setShowClientSearchDropdown(false);
      return;
    }

    try {
      setClientSearchLoading(true);
      const response = await clientsAPI.getAll({
        search: query.trim(),
        page: 1,
        limit: 20,
      });
      
      setClientSearchResults(response.clients || []);
      setShowClientSearchDropdown(true);
      setClientSearchSelectedIndex(-1);
    } catch (err) {
      console.error('Client search error:', err);
      setClientSearchResults([]);
      setShowClientSearchDropdown(false);
    } finally {
      setClientSearchLoading(false);
    }
  };

  // Handle client search input change
  const handleClientSearchChange = (value) => {
    setClientSearchQuery(value);

    // Clear previous timeout
    if (clientSearchTimeoutRef.current) {
      clearTimeout(clientSearchTimeoutRef.current);
    }

    // Only search if 3+ characters
    if (value.trim().length >= 3) {
      // Debounce search
      clientSearchTimeoutRef.current = setTimeout(() => {
        searchClients(value);
      }, 300);
    } else {
      setClientSearchResults([]);
      setShowClientSearchDropdown(false);
      setClientSearchSelectedIndex(-1);
    }
  };

  // Handle client selection from dropdown
  const handleClientSearchSelect = (client) => {
    handleClientSelect(client);
  };

  // Handle keyboard navigation for client search
  const handleClientSearchKeyDown = (e) => {
    if (!showClientSearchDropdown || clientSearchResults.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setClientSearchSelectedIndex(prev => 
        Math.min(prev + 1, clientSearchResults.length - 1)
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setClientSearchSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = clientSearchResults[clientSearchSelectedIndex];
      if (selected) {
        handleClientSearchSelect(selected);
      }
    } else if (e.key === 'Escape') {
      setShowClientSearchDropdown(false);
      setClientSearchSelectedIndex(-1);
    }
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        clientSearchContainerRef.current &&
        !clientSearchContainerRef.current.contains(event.target)
      ) {
        setShowClientSearchDropdown(false);
      }
    };

    if (showClientSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showClientSearchDropdown]);

  // Cleanup client search timeout on unmount
  useEffect(() => {
    return () => {
      if (clientSearchTimeoutRef.current) {
        clearTimeout(clientSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleAddMaterial = () => {
    setMaterials([...materials, {
      id: `temp-${Date.now()}`,
      description: '',
      quantity: 1,
      unitPrice: 0,
      partNumber: '',
      notes: '',
      costPrice: 0,
      markup: 0,
    }]);
  };

  const handleInsertMaterial = (index) => {
    const newMaterials = [...materials];
    newMaterials.splice(index + 1, 0, {
      id: `temp-${Date.now()}`,
      description: '',
      quantity: 1,
      unitPrice: 0,
      partNumber: '',
      notes: '',
      costPrice: 0,
      markup: 0,
    });
    setMaterials(newMaterials);
  };

  const handleUpdateMaterial = (index, field, value) => {
    const updated = [...materials];
    updated[index] = { ...updated[index], [field]: value };
    
    // If profit calc is enabled and cost or markup changed, calculate unit price
    if (profitCalcEnabled && (field === 'costPrice' || field === 'markup')) {
      const costPrice = parseFloat(updated[index].costPrice) || 0;
      const markup = parseFloat(updated[index].markup) || 0;
      // Calculate: cost × (1 + markup/100) = unit price, rounded to 2 decimal places
      updated[index].unitPrice = Math.round((costPrice * (1 + markup / 100)) * 100) / 100;
    }
    
    // Recalculate line total
    if (field === 'quantity' || field === 'unitPrice') {
      updated[index].lineTotal = (updated[index].quantity || 0) * (updated[index].unitPrice || 0);
    } else if (profitCalcEnabled && (field === 'costPrice' || field === 'markup')) {
      // Recalculate line total when cost/markup changes (since unitPrice is auto-calculated)
      updated[index].lineTotal = (updated[index].quantity || 0) * (updated[index].unitPrice || 0);
    }
    
    setMaterials(updated);

    // Handle autocomplete for description field
    if (field === 'description') {
      handleMaterialAutocomplete(index, value);
    }
  };

  const handleMaterialAutocomplete = (index, query) => {
    // Clear previous timeout
    if (autocompleteTimeoutRef.current[index]) {
      clearTimeout(autocompleteTimeoutRef.current[index]);
    }

    // Hide autocomplete if query is empty
    if (!query || query.trim().length < 2) {
      setMaterialAutocomplete(prev => ({
        ...prev,
        [index]: { suggestions: [], selectedIndex: -1, show: false }
      }));
      return;
    }

    // Debounce search
    autocompleteTimeoutRef.current[index] = setTimeout(async () => {
      try {
        const response = await purchasesAPI.getMaterialsLookup(query.trim(), 10);
        setMaterialAutocomplete(prev => ({
          ...prev,
          [index]: {
            suggestions: response.materials || [],
            selectedIndex: -1,
            show: true,
          }
        }));
      } catch (err) {
        console.error('Material lookup error:', err);
        setMaterialAutocomplete(prev => ({
          ...prev,
          [index]: { suggestions: [], selectedIndex: -1, show: false }
        }));
      }
    }, 300);
  };

  const handleMaterialSelect = (index, material) => {
    const updated = [...materials];
    const row = { ...updated[index] };
    const avgRaw = Number(material.avgUnitPrice) || 0;
    const avgUnit = Math.round(avgRaw * 100) / 100;
    const avgCost = Math.round(avgRaw * 1000) / 1000;

    row.description = material.description;
    row.partNumber = material.partNumber || '';

    if (profitCalcEnabled) {
      row.costPrice = avgCost;
      const markup = parseFloat(row.markup) || 0;
      row.unitPrice = Math.round((avgCost * (1 + markup / 100)) * 100) / 100;
    } else {
      row.unitPrice = avgUnit;
    }

    row.lineTotal = (row.quantity || 0) * (row.unitPrice || 0);
    updated[index] = row;
    setMaterials(updated);

    // Hide autocomplete
    setMaterialAutocomplete(prev => ({
      ...prev,
      [index]: { suggestions: [], selectedIndex: -1, show: false }
    }));
  };

  const handleMaterialAutocompleteKeyDown = (index, e) => {
    const autocomplete = materialAutocomplete[index];
    if (!autocomplete || !autocomplete.show || autocomplete.suggestions.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMaterialAutocomplete(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          selectedIndex: Math.min(
            prev[index].selectedIndex + 1,
            prev[index].suggestions.length - 1
          )
        }
      }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMaterialAutocomplete(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          selectedIndex: Math.max(prev[index].selectedIndex - 1, -1)
        }
      }));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = autocomplete.suggestions[autocomplete.selectedIndex];
      if (selected) {
        handleMaterialSelect(index, selected);
      }
    } else if (e.key === 'Escape') {
      setMaterialAutocomplete(prev => ({
        ...prev,
        [index]: { suggestions: [], selectedIndex: -1, show: false }
      }));
    }
  };

  // Materials lookup dropdown: portal + fixed position so it is not clipped by overflow-x-auto on the table wrapper
  useLayoutEffect(() => {
    let activeIndex = null;
    for (const key of Object.keys(materialAutocomplete)) {
      const idx = Number(key);
      const ac = materialAutocomplete[idx];
      if (ac?.show && ac.suggestions?.length > 0) {
        activeIndex = idx;
        break;
      }
    }
    if (activeIndex === null) {
      setMaterialSuggestCoords(null);
      return;
    }
    const updateCoords = () => {
      const el = materialDescInputRefs.current[activeIndex];
      if (!el) {
        setMaterialSuggestCoords(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const minW = 480;
      const maxW = Math.max(200, window.innerWidth - r.left - 12);
      const width = Math.min(Math.max(r.width, minW), maxW);
      setMaterialSuggestCoords({
        index: activeIndex,
        top: r.bottom + 4,
        left: r.left,
        width,
      });
    };
    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [materialAutocomplete]);

  const handleRemoveMaterial = (index) => {
    setMaterials(materials.filter((_, i) => i !== index));
    // Clean up autocomplete state for removed material
    setMaterialAutocomplete(prev => {
      const updated = { ...prev };
      delete updated[index];
      // Reindex remaining autocomplete states
      const reindexed = {};
      Object.keys(updated).forEach(key => {
        const keyNum = parseInt(key);
        if (keyNum > index) {
          reindexed[keyNum - 1] = updated[key];
        } else if (keyNum < index) {
          reindexed[keyNum] = updated[key];
        }
      });
      return reindexed;
    });
    // Clear timeout for removed material
    if (autocompleteTimeoutRef.current[index]) {
      clearTimeout(autocompleteTimeoutRef.current[index]);
      delete autocompleteTimeoutRef.current[index];
    }
    // Reindex description input refs after row removal
    const prevRefs = materialDescInputRefs.current;
    const reindexedRefs = {};
    Object.keys(prevRefs).forEach((key) => {
      const keyNum = parseInt(key, 10);
      if (Number.isNaN(keyNum)) return;
      if (keyNum > index) reindexedRefs[keyNum - 1] = prevRefs[key];
      else if (keyNum < index) reindexedRefs[keyNum] = prevRefs[key];
    });
    materialDescInputRefs.current = reindexedRefs;
  };

  const handleAddLabour = () => {
    setLabour([...labour, {
      id: `temp-${Date.now()}`,
      description: '',
      hours: 1,
      hourlyRate: 0,
      notes: '',
    }]);
  };

  const handleInsertLabour = (index) => {
    const newLabour = [...labour];
    newLabour.splice(index + 1, 0, {
      id: `temp-${Date.now()}`,
      description: '',
      hours: 1,
      hourlyRate: 0,
      notes: '',
    });
    setLabour(newLabour);
  };

  const handleUpdateLabour = (index, field, value) => {
    const updated = [...labour];
    updated[index] = { ...updated[index], [field]: value };
    
    // Recalculate line total
    if (field === 'hours' || field === 'hourlyRate') {
      updated[index].lineTotal = (updated[index].hours || 0) * (updated[index].hourlyRate || 0);
    }
    
    setLabour(updated);
  };

  const handleRemoveLabour = (index) => {
    setLabour(labour.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const materialsSubtotal = materials.reduce((sum, m) => sum + (m.lineTotal || 0), 0);
    const labourSubtotal = labour.reduce((sum, l) => sum + (l.lineTotal || 0), 0);
    const subtotal = materialsSubtotal + labourSubtotal;

    // VAT on net line totals (including discounts / negative lines), same as server calculateInvoiceTotals
    const vatAmount = (subtotal * formData.vatRate) / 100;
    const total = subtotal + vatAmount;

    return { materialsSubtotal, labourSubtotal, subtotal, vatAmount, total };
  };

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!initialFormStateRef.current) return false;

    // Compare form data
    const current = formData;
    const initial = initialFormStateRef.current;
    
    if (
      current.clientId !== initial.clientId ||
      current.invoiceNum !== initial.invoiceNum ||
      current.reference !== initial.reference ||
      current.dateCreated !== initial.dateCreated ||
      current.dateIssued !== initial.dateIssued ||
      current.dueDate !== initial.dueDate ||
      current.vatRate !== initial.vatRate ||
      current.notes !== initial.notes ||
      current.terms !== initial.terms ||
      current.privateNotes !== initial.privateNotes
    ) {
      return true;
    }

    // Compare client
    if (selectedClient?.id !== initialClientRef.current?.id) {
      return true;
    }

    // Compare materials
    const materialsStr = JSON.stringify(materials.map(m => ({
      description: m.description,
      quantity: parseFloat(m.quantity) || 0,
      unitPrice: parseFloat(m.unitPrice) || 0,
      partNumber: m.partNumber,
      notes: m.notes,
      costPrice: parseFloat(m.costPrice) || 0,
      markup: parseFloat(m.markup) || 0,
    })));
    const initialMaterialsStr = JSON.stringify(initialMaterialsRef.current);
    if (materialsStr !== initialMaterialsStr) {
      return true;
    }

    // Compare labour
    const labourStr = JSON.stringify(labour.map(l => ({
      description: l.description,
      hours: parseFloat(l.hours) || 0,
      hourlyRate: parseFloat(l.hourlyRate) || 0,
      notes: l.notes,
    })));
    const initialLabourStr = JSON.stringify(initialLabourRef.current);
    if (labourStr !== initialLabourStr) {
      return true;
    }

    return false;
  }, [formData, selectedClient, materials, labour]);

  // Warn user before navigating away
  const handleNavigationAttempt = (callback) => {
    if (hasUnsavedChanges()) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave? Your changes will be lost.');
      if (!confirmed) {
        return false;
      }
    }
    callback();
    return true;
  };

  // Browser beforeunload handler
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Check for unsaved changes inline to avoid dependency issues
      if (!initialFormStateRef.current) return;
      
      const current = formData;
      const initial = initialFormStateRef.current;
      
      const formChanged = (
        current.clientId !== initial.clientId ||
        current.invoiceNum !== initial.invoiceNum ||
        current.reference !== initial.reference ||
        current.dateCreated !== initial.dateCreated ||
        current.dateIssued !== initial.dateIssued ||
        current.dueDate !== initial.dueDate ||
        current.vatRate !== initial.vatRate ||
        current.notes !== initial.notes ||
        current.terms !== initial.terms ||
        current.privateNotes !== initial.privateNotes ||
        selectedClient?.id !== initialClientRef.current?.id
      );
      
      const materialsChanged = JSON.stringify(materials.map(m => ({
        description: m.description,
        quantity: parseFloat(m.quantity) || 0,
        unitPrice: parseFloat(m.unitPrice) || 0,
        partNumber: m.partNumber,
        notes: m.notes,
        costPrice: parseFloat(m.costPrice) || 0,
        markup: parseFloat(m.markup) || 0,
      }))) !== JSON.stringify(initialMaterialsRef.current);
      
      const labourChanged = JSON.stringify(labour.map(l => ({
        description: l.description,
        hours: parseFloat(l.hours) || 0,
        hourlyRate: parseFloat(l.hourlyRate) || 0,
        notes: l.notes,
      }))) !== JSON.stringify(initialLabourRef.current);
      
      if (formChanged || materialsChanged || labourChanged) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData, selectedClient, materials, labour]);

  // Keep ref updated with latest check function
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Register unsaved changes check with context
  useEffect(() => {
    registerCheck(() => {
      if (hasUnsavedChangesRef.current) {
        return hasUnsavedChangesRef.current();
      }
      return false;
    });
    return () => {
      unregisterCheck();
    };
  }, [registerCheck, unregisterCheck]);

  // Load available certificates when modal opens
  useEffect(() => {
    if (showCertificateLinkModal && selectedClient && isEditMode && id) {
      const loadCertificates = async () => {
        try {
          setCertificatesLoading(true);
          // Fetch all certificates for this client
          const certs = await certificatesAPI.getAll({
            clientId: selectedClient.id || selectedClient.client_id,
            isArchived: false,
            limit: 100,
          });
          
          // Filter out already linked certificates
          const linkedIds = linkedCertificates.map(c => c.id);
          const available = certs.data || certs || [];
          setAvailableCertificates(available.filter(c => !linkedIds.includes(c.id)));
        } catch (err) {
          console.error('Error loading certificates:', err);
          setError(err.message || 'Failed to load certificates');
        } finally {
          setCertificatesLoading(false);
        }
      };
      
      loadCertificates();
    }
  }, [showCertificateLinkModal, selectedClient, id, isEditMode, linkedCertificates]);

  // Scroll to top when invoice ID changes (e.g., after creating new invoice)
  useEffect(() => {
    if (id && isEditMode) {
      // Multiple scroll attempts to ensure we get to the top
      // React Router navigation and rendering can take time
      window.scrollTo({ top: 0, behavior: 'auto' });
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }, 100);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 300);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }, 500);
    }
  }, [id, isEditMode]);

  // Legacy: /tenant/invoices/:id?send=1 → dedicated send page
  useEffect(() => {
    if (!isEditMode || !id || legacySendIntent !== '1') return;
    navigate(`/tenant/invoices/${id}/send`, { replace: true });
  }, [isEditMode, id, legacySendIntent, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Scroll to top immediately when button is clicked to show notifications
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (!formData.clientId) {
      setError('Please select a client');
      return;
    }

    if (!formData.invoiceNum || !formData.invoiceNum.trim()) {
      setError('Invoice number is required');
      return;
    }

    // Check for invoice number error before submitting
    if (invoiceNumError) {
      setError('Please fix the invoice number error before saving');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Read VAT rate from the input ref so we use the current DOM value on submit.
      // This avoids the value reverting to 20% when the user changes VAT and clicks
      // Save before React has committed the state update.
      const rawVat = vatRateInputRef.current?.value;
      const vatRateForSubmit = rawVat !== undefined && rawVat !== '' && !Number.isNaN(parseFloat(rawVat))
        ? parseFloat(rawVat)
        : (formData.vatRate ?? 20);

      const invoiceData = {
        clientId: formData.clientId,
        invoiceNum: formData.invoiceNum,
        reference: formData.reference,
        dateCreated: formData.dateCreated,
        dateIssued: formData.dateIssued || null,
        dueDate: formData.dueDate || null,
        vatRate: vatRateForSubmit,
        notes: formData.notes,
        terms: formData.terms,
        privateNotes: formData.privateNotes,
        materials: materials.map(m => ({
          description: m.description,
          quantity: parseFloat(m.quantity) || 0,
          unitPrice: parseFloat(m.unitPrice) || 0,
          partNumber: m.partNumber,
          notes: m.notes,
          costPrice: parseFloat(m.costPrice) || 0,
          markup: parseFloat(m.markup) || 0,
        })),
        labour: labour.map(l => ({
          description: l.description,
          hours: parseFloat(l.hours) || 0,
          hourlyRate: parseFloat(l.hourlyRate) || 0,
          notes: l.notes,
        })),
      };

      if (isEditMode) {
        await invoicesAPI.update(id, invoiceData);
        
        // Update initial state after successful save
        initialFormStateRef.current = { ...formData };
        initialMaterialsRef.current = JSON.parse(JSON.stringify(materials));
        initialLabourRef.current = JSON.parse(JSON.stringify(labour));
        initialClientRef.current = selectedClient;

        // Scroll to top and show success notification
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSaveSuccess(true);
        setTimeout(() => {
          setSaveSuccess(false);
        }, 5000); // Hide after 5 seconds
      } else {
        const response = await invoicesAPI.create(invoiceData);
        // API returns { message: '...', invoice: {...} } or just { id, ... }
        const invoice = response?.invoice || response;
        const invoiceId = invoice?.id;
        
        // If creating a new invoice, show success message, wait, then navigate to edit mode
        if (invoiceId) {
          // Show success notification immediately
          setSaveSuccess(true);
          
          // Wait 2.5 seconds before navigating to edit mode (slow reload effect)
          setTimeout(async () => {
            try {
              // Update initial state before navigating
              initialFormStateRef.current = { ...formData };
              initialMaterialsRef.current = JSON.parse(JSON.stringify(materials));
              initialLabourRef.current = JSON.parse(JSON.stringify(labour));
              initialClientRef.current = selectedClient;
              
              // Scroll to top immediately before navigation
              window.scrollTo({ top: 0, behavior: 'auto' });
              
              // Navigate to edit mode
              navigate(`/tenant/invoices/${invoiceId}`, { replace: true });
              
              // Wait a moment for React Router to complete navigation and DOM to update
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // Scroll to top again after navigation
              window.scrollTo({ top: 0, behavior: 'auto' });
              
              // Fetch the invoice to update initial state with server data
              await fetchInvoice();
              
              // Scroll to top after fetch completes to ensure we're at the top
              // Use multiple attempts to ensure it sticks
              window.scrollTo({ top: 0, behavior: 'auto' });
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }, 50);
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'auto' });
              }, 200);
              
              // Keep success message visible for a bit after loading
              setSaveSuccess(true);
              setTimeout(() => {
                setSaveSuccess(false);
              }, 5000); // Hide after 5 seconds
            } catch (fetchErr) {
              console.error('Error fetching invoice after creation:', fetchErr);
              setError('Invoice created but failed to load. Please refresh the page.');
              // Scroll to top on error too
              window.scrollTo({ top: 0, behavior: 'auto' });
            } finally {
              // Set saving to false after navigation completes
              setSaving(false);
              // Final scroll to top
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'auto' });
              }, 100);
            }
          }, 2500); // 2.5 second delay for slow reload effect
          
          // Don't set saving to false here - it will be set in the setTimeout callback after navigation
          // The finally block will handle it for edit mode, but for create mode we need to let it stay true
          // during the delay period
        } else {
          // If no invoice ID, something went wrong
          console.error('Invoice creation response missing ID:', response);
          setError('Invoice was created but could not retrieve the invoice ID. Please refresh the page.');
          setSaving(false);
        }
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to save invoice';
      setError(errorMessage);
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // If it's an invoice number duplicate error, also set the field error
      if (errorMessage.toLowerCase().includes('invoice number') && errorMessage.toLowerCase().includes('already exists')) {
        setInvoiceNumError('This invoice number is already in use');
      }
      
      console.error('Save error:', err);
    } finally {
      // For edit mode, set saving to false immediately after update
      // For create mode with successful creation, saving will remain true during the delay
      // and be set to false in the setTimeout callback after navigation
      if (isEditMode) {
        setSaving(false);
      }
      // Note: For create mode, if invoiceId exists, saving stays true and is set to false
      // in the setTimeout callback. If invoiceId doesn't exist, we already set it to false above.
    }
  };

  const handlePrint = async () => {
    if (!id) {
      setError('Please save the invoice first to print');
      return;
    }

    try {
      setError('');
      const result = await invoicesAPI.generateLink(id, 1); // Generate link valid for 1 day
      // Use HTML endpoint that uses the same template as PDF
      // Add ?print parameter to trigger auto-print in the template
      const url = `${window.location.origin}/api/portal/invoice/${result.token}/html?print`;
      const printWindow = window.open(url, '_blank');
      
      // Focus the window (the print dialog will be triggered automatically by the page script)
      if (printWindow) {
        printWindow.focus();
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError(err.message || 'Failed to generate print link');
      }
      console.error('Print error:', err);
    }
  };

  const totals = calculateTotals();
  const payableBalanceDue = Math.max(0, totals.total - (invoiceAmountPaid ?? 0));
  const displayInvoiceStatus = useMemo(
    () => getEffectiveInvoiceStatus({ status: invoiceStatus, dueDate: formData.dueDate }),
    [invoiceStatus, formData.dueDate]
  );
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const getClientDisplayName = (client) => {
    if (!client) return 'Select client';
    // Handle both snake_case (from clients API) and camelCase (from invoices API)
    const clientType = client.clientType || client.client_type;
    if (clientType === 'company') {
      return client.companyName || client.company_name || 'Unnamed Company';
    }
    const firstName = client.firstName || client.first_name || '';
    const lastName = client.lastName || client.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'Unnamed Individual';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-sm text-gray-500">Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                {isEditMode ? 'Edit Invoice' : 'New Invoice'}
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                {isEditMode ? 'Update invoice details' : 'Create a new invoice'}
              </p>
            </div>
            {/* Status Display (only in edit mode) */}
            {isEditMode && invoiceStatus && (
              <div className="flex flex-wrap items-center justify-end gap-2 ml-4">
                {invoiceStatus !== 'draft' && payableBalanceDue > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPaymentModal(true)}
                  >
                    Log payment
                  </Button>
                )}
                {(invoiceAmountPaid ?? 0) > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPaymentHistoryModal(true)}
                  >
                    Payment History
                  </Button>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(displayInvoiceStatus)}`}>
                    {formatStatus(displayInvoiceStatus)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          {/* Success Message */}
          {saveSuccess && (
            <div className="bg-[var(--ds-status-success-bg)] border border-[var(--ds-status-success)]/30 rounded-[var(--ds-radius-md)] p-4 flex items-center justify-between">
              <p className="text-body text-[var(--ds-status-success)] font-medium">
                ✓ Invoice {isEditMode ? 'updated' : 'created'} successfully!
              </p>
              <button
                onClick={() => setSaveSuccess(false)}
                className="text-[var(--ds-status-success)] hover:text-[var(--ds-status-success)]"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          
          {/* Error Message */}
          {error && (
            <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)] p-4">
              <p className="text-body text-[var(--ds-status-danger)]">{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <Card>
            <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h2 font-medium text-[var(--ds-text-primary)]">Invoice Details</h2>
              {isEditMode && (
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => setShowCertificateLinkModal(true)}
                  className="text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 flex items-center gap-2"
                >
                  <Link className="h-4 w-4" />
                  Link Certificates
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-[45%_20%_35%] gap-0">
              {/* Left Column: Client Selection */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300">
                    Client <span className="text-[var(--ds-status-danger)]">*</span>
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => {
                      if (selectedClient) {
                        // "Modify client" behaviour – clear current client so a new one can be chosen
                        setSelectedClient(null);
                        setFormData(prev => ({ ...prev, clientId: '' }));
                        setClientSearchQuery('');
                        if (isEditMode) {
                          setShowClientPicker(false);
                        }
                      } else {
                        // "+ New Client" behaviour – open slide-over
                        setShowNewClientSlideOver(true);
                      }
                    }}
                    className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                  >
                    {selectedClient ? 'Modify client' : '+ New Client'}
                  </Button>
                </div>
                <div className="flex flex-col space-y-2">
                  {!selectedClient && (
                    // No client selected: Show searchable input (same for both create and edit modes)
                    <div className="relative" ref={clientSearchContainerRef}>
                      <Input
                        type="text"
                        value={clientSearchQuery}
                        onChange={(e) => handleClientSearchChange(e.target.value)}
                        onKeyDown={handleClientSearchKeyDown}
                        onFocus={() => {
                          if (clientSearchQuery.trim().length >= 3 && clientSearchResults.length > 0) {
                            setShowClientSearchDropdown(true);
                          }
                        }}
                        placeholder="Type at least 3 characters to search for a client..."
                        className="h-8"
                      />
                      {/* Client Search Dropdown */}
                      {showClientSearchDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] shadow-[var(--ds-shadow-lg)] max-h-60 overflow-y-auto">
                          {clientSearchLoading ? (
                            <div className="px-4 py-3 text-center text-body text-[var(--ds-text-muted)]">
                              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--ds-accent-primary)]"></div>
                              <span className="ml-2">Searching...</span>
                            </div>
                          ) : clientSearchResults.length > 0 ? (
                            clientSearchResults.map((client, index) => {
                              const clientType = client.clientType || client.client_type;
                              const displayName = clientType === 'company' 
                                ? (client.companyName || client.company_name || 'Unnamed Company')
                                : `${client.firstName || client.first_name || ''} ${client.lastName || client.last_name || ''}`.trim() || 'Unnamed Individual';
                              return (
                                <div
                                  key={client.id}
                                  onClick={() => handleClientSearchSelect(client)}
                                  className={`px-4 py-3 cursor-pointer hover:bg-[var(--ds-control-field-bg)] transition-colors duration-180 ${
                                    index === clientSearchSelectedIndex ? 'bg-[var(--ds-control-field-bg)]' : ''
                                  }`}
                                >
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-8 w-8">
                                      <div className="h-8 w-8 rounded-full bg-[var(--ds-accent-primary-bg)] flex items-center justify-center text-[var(--ds-accent-primary)] font-bold text-micro">
                                        {displayName.charAt(0).toUpperCase()}
                                      </div>
                                    </div>
                                    <div className="ml-3 flex-1">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <div className="text-body font-medium text-[var(--ds-text-primary)]">
                                            {displayName}
                                          </div>
                                          {client.email && (
                                            <div className="text-meta text-[var(--ds-text-muted)]">{client.email}</div>
                                          )}
                                        </div>
                                        <div>
                                          {clientType === 'company' ? (
                                            <Building2 className="h-4 w-4 text-[var(--ds-text-muted)]" />
                                          ) : (
                                            <User className="h-4 w-4 text-[var(--ds-text-muted)]" />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : clientSearchQuery.trim().length >= 3 ? (
                            <div className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                              No clients found
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Client Details (Read-only) - Show when client is selected */}
                {selectedClient && (() => {
                  // Handle both snake_case and camelCase field names
                  const email = selectedClient.email;
                  const phone = selectedClient.phone;
                  const mobile = selectedClient.mobile;
                  const addressLine1 = selectedClient.address_line1 || selectedClient.addressLine1;
                  const addressLine2 = selectedClient.address_line2 || selectedClient.addressLine2;
                  const city = selectedClient.city;
                  const county = selectedClient.county;
                  const postcode = selectedClient.postcode;
                  const clientType = selectedClient.clientType || selectedClient.client_type;
                  const displayName =
                    clientType === 'company'
                      ? (selectedClient.companyName || selectedClient.company_name || 'Unnamed Company')
                      : (`${selectedClient.firstName || selectedClient.first_name || ''} ${selectedClient.lastName || selectedClient.last_name || ''}`.trim() ||
                        'Unnamed Individual');
                  
                  const hasContact = email || phone || mobile;
                  const hasAddress = addressLine1 || addressLine2 || city || county || postcode;
                  
                  if (!hasContact && !hasAddress) return null;
                  
                  return (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-md">
                      {/* Address / Client Name */}
                      {hasAddress && (
                        <div className={hasContact ? "mb-3" : ""}>
                          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">{displayName}</h4>
                          <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                            {addressLine1 && (
                              <div className="flex items-start">
                                <span>{addressLine1}</span>
                              </div>
                            )}
                            {addressLine2 && (
                              <div>{addressLine2}</div>
                            )}
                            {(city || county) && (
                              <div>
                                {city && city}
                                {city && county && ', '}
                                {county && county}
                              </div>
                            )}
                            {postcode && (
                              <div>{postcode}</div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Contact Information */}
                      {hasContact && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">Contact</h4>
                          <div className="space-y-1.5 text-sm">
                            {email && (
                              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                <Mail className="h-3.5 w-3.5" />
                                <span>{email}</span>
                              </div>
                            )}
                            {phone && (
                              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                <Phone className="h-3.5 w-3.5" />
                                <span>{phone}</span>
                              </div>
                            )}
                            {mobile && (
                              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                <Phone className="h-3.5 w-3.5" />
                                <span>{mobile}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Empty div to push the invoice details fields to the right */}
              <div></div>

              {/* Right Column: Invoice Details Fields */}
              <div className="space-y-4">
                {/* Invoice Number */}
                <div>
                  <div className="flex items-start gap-5">
                    <label className="text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right">
                      Invoice Number <span className="text-[var(--ds-status-danger)]">*</span>
                    </label>

                    <div className="ml-auto flex flex-col">
                      <div className="flex items-end gap-2">
                        <input
                          type="text"
                          value={formData.invoiceNum}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setFormData(prev => ({ ...prev, invoiceNum: newValue }));
                            if (invoiceNumError) setInvoiceNumError('');
                          }}
                          onBlur={async () => {
                            const trimmedValue = formData.invoiceNum.trim();
                            if (!trimmedValue) {
                              setInvoiceNumError('');
                              return;
                            }

                            if (isEditMode && trimmedValue === initialFormStateRef.current?.invoiceNum) {
                              setInvoiceNumError('');
                              if (!isEditingInvoiceNum) setIsEditingInvoiceNum(false);
                              return;
                            }

                            try {
                              setCheckingInvoiceNum(true);
                              const result = await invoicesAPI.checkNumber(trimmedValue, isEditMode ? id : undefined);
                              if (!result.available) {
                                setInvoiceNumError(result.message || 'This invoice number is already in use');
                              } else {
                                setInvoiceNumError('');
                              }
                            } catch (err) {
                              console.error('Error checking invoice number:', err);
                            } finally {
                              setCheckingInvoiceNum(false);
                            }
                          }}
                          disabled={isEditMode && !isEditingInvoiceNum}
                          required
                          className={`w-auto px-2 py-1 text-sm bg-transparent border-0 border-b ${
                            invoiceNumError 
                              ? 'border-red-400 dark:border-red-500' 
                              : 'border-gray-300 dark:border-gray-600'
                          } focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-0 ${
                            isEditMode && !isEditingInvoiceNum
                              ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                              : 'text-gray-900 dark:text-gray-100'
                          } transition-colors`}
                        />

                        {isEditMode && !isEditingInvoiceNum && (
                          <button
                            type="button"
                            onClick={() => {
                              setIsEditingInvoiceNum(true);
                              setInvoiceNumError('');
                            }}
                            className="inline-flex items-center p-0 m-0 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </div>

                      {checkingInvoiceNum && isEditingInvoiceNum && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Checking availability...</p>
                      )}
                      {invoiceNumError && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{invoiceNumError}</p>
                      )}
                      {!invoiceNumError && !checkingInvoiceNum && formData.invoiceNum.trim() && isEditingInvoiceNum && (
                        <p className="mt-1 text-xs text-green-600 dark:text-green-400">✓ Invoice number available</p>
                      )}
                    </div>
                  </div>

                </div>

                {/* Reference */}
                <div className="flex items-end gap-5">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right">
                    Reference
                  </label>
                  <input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                    className="ml-auto w-2/4 px-2 py-1 h-8 text-sm bg-transparent border-0 border-b border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100 transition-colors"
                  />
                </div>

                {/* Date Created */}
                <div className="flex items-end gap-5">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right">
                    Date Created
                  </label>
                  <input
                    type="date"
                    value={formData.dateCreated}
                    onChange={(e) => setFormData(prev => ({ ...prev, dateCreated: e.target.value }))}
                    className="ml-auto w-2/4 px-2 py-1 h-8 text-sm bg-transparent border-0 border-b border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100 transition-colors"
                  />
                </div>

                {/* Date Issued */}
                <div className="flex items-end gap-5">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right">
                    Date Issued
                  </label>
                  <input
                    type="date"
                    value={formData.dateIssued}
                    onChange={(e) => {
                      const dateIssued = e.target.value;
                      setFormData((prev) => {
                        if (!dateIssued) {
                          return { ...prev, dateIssued: '' };
                        }
                        const termsDays = getEffectiveInvoicePaymentTermsDays(selectedClient, defaultPaymentDueDays);
                        return {
                          ...prev,
                          dateIssued,
                          dueDate: addDaysToIsoDate(dateIssued, termsDays),
                        };
                      });
                    }}
                    className="ml-auto w-2/4 px-2 py-1 h-8 text-sm bg-transparent border-0 border-b border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100 transition-colors"
                  />
                </div>

                {/* Due Date */}
                <div className="flex items-end gap-5">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="ml-auto w-2/4 px-2 py-1 h-8 text-sm bg-transparent border-0 border-b border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100 transition-colors"
                  />
                </div>                
              </div>
            </div>
            </CardContent>
          </Card>

          {/* Materials & Labour Section */}
          <Card>
            <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="inline-flex rounded-md shadow-sm" role="group">
                <TooltipProvider>
                  <Tooltip content="This function allows you to calculate markups on materials, this will not be visable to your clients. Markup is calculated as a percentage of the cost price. NOTE: Enabling this will disable the unit price field.">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setProfitCalcEnabled(!profitCalcEnabled)}
                      className="rounded-r-none border-r-0 text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300"
                    >
                      {profitCalcEnabled ? 'Disable Markup Calculator' : 'Enable Markup Calculator'}
                    </Button>
                  </Tooltip>
                </TooltipProvider>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPriceLookupSlideOver(true)}
                  className="rounded-l-none text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300"
                >
                  Lookup last purchased price
                </Button>
              </div>
              <div className="inline-flex rounded-md shadow-sm" role="group">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddMaterial}
                  className="rounded-r-none border-r-0 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  + Add Material
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddLabour}
                  className="rounded-l-none text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  + Add Labour
                </Button>
              </div>
            </div>

            {/* Materials Subsection */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Materials</h3>
              {materials.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No materials added yet</p>
              ) : (
                <div className="relative pr-8 pl-8">
                  {/* Plus buttons positioned outside the table border on the left */}
                  <div className="absolute left-0 flex flex-col z-10" style={{ top: '42px' }}>
                    {materials.map((material, index) => (
                      <div
                        key={`plus-${material.id || index}`}
                        className="flex items-center justify-end"
                        style={{ height: '42px' }}
                      >
                        <button
                          type="button"
                          onClick={() => handleInsertMaterial(index)}
                          className="flex items-center justify-end
                                      text-blue-600 dark:text-blue-400 
                                      hover:text-blue-800 dark:hover:text-blue-300 
                                      hover:bg-blue-50 dark:hover:bg-blue-900/20 
                                      rounded p-1 transition-colors"
                          title="Add Material"
                          style={{ marginLeft: '0px' }}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Description</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Part No</th>
                        {profitCalcEnabled ? (
                          <>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Cost (Ex-VAT)</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Markup (%)</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Unit Price</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Qty</th>
                          </>
                        ) : (
                          <>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Qty</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Unit Price</th>
                          </>
                        )}
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materials.map((material, index) => (
                        <tr key={material.id || index} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="py-2 px-4">
                            <input
                              ref={(el) => {
                                if (el) materialDescInputRefs.current[index] = el;
                                else delete materialDescInputRefs.current[index];
                              }}
                              type="text"
                              value={material.description}
                              onChange={(e) => handleUpdateMaterial(index, 'description', e.target.value)}
                              onKeyDown={(e) => handleMaterialAutocompleteKeyDown(index, e)}
                              onBlur={() => {
                                // Delay hiding to allow click events (portal list uses onMouseDown preventDefault)
                                setTimeout(() => {
                                  setMaterialAutocomplete(prev => ({
                                    ...prev,
                                    [index]: { ...prev[index], show: false }
                                  }));
                                }, 200);
                              }}
                              className="w-full px-0 py-1 bg-transparent border-none text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                              placeholder="Item description"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <input
                              type="text"
                              value={material.partNumber || ''}
                              onChange={(e) => handleUpdateMaterial(index, 'partNumber', e.target.value)}
                              className="w-full px-0 py-1 bg-transparent border-none text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                              placeholder="—"
                            />
                          </td>
                          {profitCalcEnabled ? (
                            <>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  step="0.001"
                                  value={material.costPrice || 0}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    // Round to 3 decimal places
                                    const rounded = Math.round(value * 1000) / 1000;
                                    handleUpdateMaterial(index, 'costPrice', rounded);
                                  }}
                                  className="no-spinner w-full px-0 py-1 bg-transparent border-none text-sm text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0"
                                />
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  step="0.1"
                                  value={material.markup || 0}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    // Round to 1 decimal place
                                    const rounded = Math.round(value * 10) / 10;
                                    handleUpdateMaterial(index, 'markup', rounded);
                                  }}
                                  className="no-spinner w-full px-0 py-1 bg-transparent border-none text-sm text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0"
                                />
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={material.unitPrice ? Math.round(material.unitPrice * 100) / 100 : 0}
                                  disabled
                                  className="no-spinner w-full px-0 py-1 bg-transparent border-none text-sm text-gray-500 dark:text-gray-400 text-right focus:outline-none focus:ring-0 cursor-not-allowed"
                                />
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  step="1"
                                  value={material.quantity}
                                  onChange={(e) => handleUpdateMaterial(index, 'quantity', parseFloat(e.target.value) || 0)}
                                  className="no-spinner w-full px-0 py-1 bg-transparent border-none text-sm text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0"
                                />
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  step="1"
                                  value={material.quantity}
                                  onChange={(e) => handleUpdateMaterial(index, 'quantity', parseFloat(e.target.value) || 0)}
                                  className="no-spinner w-full px-0 py-1 bg-transparent border-none text-sm text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0"
                                />
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={material.unitPrice}
                                  onChange={(e) => handleUpdateMaterial(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                  className="no-spinner w-full px-0 py-1 bg-transparent border-none text-sm text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0"
                                />
                              </td>
                            </>
                          )}
                          <td className="py-2 px-4">
                            <span className="text-sm text-gray-900 dark:text-gray-100 font-medium text-right block">
                              {formatCurrency(material.lineTotal || 0)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {/* X buttons positioned outside the table border */}
                  <div className="absolute right-0 flex flex-col z-10" style={{ top: '42px' }}>
                    {materials.map((material, index) => (
                      <div
                        key={material.id || index}
                        className="flex items-center justify-start"
                        style={{ height: '42px' }}
                      >
                        <button
                          type="button"
                          onClick={() => handleRemoveMaterial(index)}
                          className="flex items-center justify-start
                                      text-red-600 dark:text-red-400 
                                      hover:text-red-800 dark:hover:text-red-300 
                                      hover:bg-red-50 dark:hover:bg-red-900/20 
                                      rounded p-1 transition-colors"
                          title="Remove"
                          style={{ marginRight: '0px' }}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {typeof document !== 'undefined' &&
              materialSuggestCoords != null &&
              materialAutocomplete[materialSuggestCoords.index]?.show &&
              (materialAutocomplete[materialSuggestCoords.index]?.suggestions?.length ?? 0) > 0 &&
              createPortal(
                <div
                  className="fixed z-[200] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto text-[10px] leading-snug"
                  style={{
                    top: materialSuggestCoords.top,
                    left: materialSuggestCoords.left,
                    width: materialSuggestCoords.width,
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {materialAutocomplete[materialSuggestCoords.index].suggestions.map((suggestion, sugIndex) => (
                    <div
                      key={`${suggestion.description}-${suggestion.partNumber || sugIndex}`}
                      role="option"
                      aria-selected={sugIndex === materialAutocomplete[materialSuggestCoords.index].selectedIndex}
                      onClick={() => handleMaterialSelect(materialSuggestCoords.index, suggestion)}
                      className={`px-3 py-1.5 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 ${
                        sugIndex === materialAutocomplete[materialSuggestCoords.index].selectedIndex
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : ''
                      }`}
                      title={`Last purchased: ${suggestion.lastPurchasedDate ? new Date(suggestion.lastPurchasedDate).toLocaleDateString('en-GB') : 'N/A'} | Avg price: £${suggestion.avgUnitPrice.toFixed(2)}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-gray-100 break-words">
                            {suggestion.description}
                          </div>
                          {suggestion.partNumber && (
                            <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                              Part: {suggestion.partNumber}
                            </div>
                          )}
                        </div>
                        <div
                          className="shrink-0 text-gray-500 dark:text-gray-400 tabular-nums"
                          title={`Last purchased: ${suggestion.lastPurchasedDate ? new Date(suggestion.lastPurchasedDate).toLocaleDateString('en-GB') : 'N/A'}`}
                        >
                          £{suggestion.avgUnitPrice.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>,
                document.body
              )}

            {/* Labour Subsection */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                Labour
                <TooltipProvider>
                  <Tooltip content="Click to view rate calculations guide">
                    <button
                      type="button"
                      onClick={() => setShowLabourInfoSlideOver(true)}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      aria-label="Rate calculations information"
                    >
                      <Info className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    </button>
                  </Tooltip>
                </TooltipProvider>
              </h3>
              {labour.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No labour items added yet</p>
              ) : (
                <div className="relative pr-8 pl-8">
                  {/* Plus buttons positioned outside the table border on the left */}
                  <div className="absolute left-0 flex flex-col z-10" style={{ top: '42px' }}>
                    {labour.map((labourItem, index) => (
                      <div
                        key={`plus-${labourItem.id || index}`}
                        className="flex items-center justify-end"
                        style={{ height: '42px' }}
                      >
                        <button
                          type="button"
                          onClick={() => handleInsertLabour(index)}
                          className="flex items-center justify-end
                                      text-blue-600 dark:text-blue-400 
                                      hover:text-blue-800 dark:hover:text-blue-300 
                                      hover:bg-blue-50 dark:hover:bg-blue-900/20 
                                      rounded p-1 transition-colors"
                          title="Add Labour"
                          style={{ marginLeft: '0px' }}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Description</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Qty</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Rate</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {labour.map((labourItem, index) => (
                        <tr key={labourItem.id || index} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="py-2 px-4">
                            <input
                              type="text"
                              value={labourItem.description}
                              onChange={(e) => handleUpdateLabour(index, 'description', e.target.value)}
                              className="w-full px-0 py-1 bg-transparent border-none text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                              placeholder="Work description"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <input
                              type="number"
                              step="0.25"
                              value={labourItem.hours}
                              onChange={(e) => handleUpdateLabour(index, 'hours', parseFloat(e.target.value) || 0)}
                              className="no-spinner w-full px-0 py-1 bg-transparent border-none text-sm text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <input
                              type="number"
                              step="0.01"
                              value={labourItem.hourlyRate}
                              onChange={(e) => handleUpdateLabour(index, 'hourlyRate', parseFloat(e.target.value) || 0)}
                              className="no-spinner w-full px-0 py-1 bg-transparent border-none text-sm text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <span className="text-sm text-gray-900 dark:text-gray-100 font-medium text-right block">
                              {formatCurrency(labourItem.lineTotal || 0)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {/* X buttons positioned outside the table border */}
                  <div className="absolute right-0 flex flex-col z-10" style={{ top: '42px' }}>
                    {labour.map((labourItem, index) => (
                      <div
                        key={labourItem.id || index}
                        className="flex items-center justify-start"
                        style={{ height: '42px' }}
                      >
                        <button
                          type="button"
                          onClick={() => handleRemoveLabour(index)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1 transition-colors"
                          title="Remove"
                          style={{ marginRight: '0px' }}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </CardContent>
          </Card>

          {/* Linked Certificates */}
          {isEditMode && linkedCertificates.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h2 className="text-h2 font-medium text-[var(--ds-text-primary)] mb-4">Linked Certificates</h2>
                <div className="space-y-2">
                  {linkedCertificates.map((cert) => (
                    <div
                      key={cert.id}
                      className="flex items-center justify-between p-3 bg-[var(--ds-control-field-bg)] rounded-md border border-[var(--ds-border-subtle)]"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-[var(--ds-text-muted)]" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[var(--ds-text-primary)]">
                              {cert.certificateNum}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded bg-[var(--ds-control-field-bg)] text-[var(--ds-text-muted)] uppercase">
                              {cert.certificateType}
                            </span>
                          </div>
                          {cert.jobReference && (
                            <p className="text-xs text-[var(--ds-text-muted)] mt-1">
                              Job: {cert.jobReference}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[var(--ds-text-muted)] mt-3">
                  These certificates will be available for download by the client when the invoice status is "sent".
                </p>
              </CardContent>
            </Card>
          )}

          
          {/* Additional Information and Invoice Totals - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-[68%_30%] gap-6">
            {/* Notes & Terms Card - Left (70%) */}
            <Card>
            <CardContent className="p-6">
            <h2 className="text-h2 font-medium text-[var(--ds-text-primary)] mb-4">Additional Information</h2>
            
            {/* Tabs */}
            <div className="border-b border-[var(--ds-border-divider)] mb-4">
              <nav className="-mb-px flex">
                <button
                  type="button"
                  onClick={() => setActiveInfoTab('notes')}
                  className={`py-3 px-4 border-b-2 font-medium text-body ${
                    activeInfoTab === 'notes'
                      ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                      : 'border-transparent text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-border-subtle)]'
                  }`}
                >
                  Information & Payment
                </button>
                <button
                  type="button"
                  onClick={() => setActiveInfoTab('terms')}
                  className={`py-3 px-4 border-b-2 font-medium text-body ${
                    activeInfoTab === 'terms'
                      ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                      : 'border-transparent text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-border-subtle)]'
                  }`}
                >
                  Terms & Conditions
                </button>
                <button
                  type="button"
                  onClick={() => setActiveInfoTab('privateNotes')}
                  className={`py-3 px-4 border-b-2 font-medium text-body ${
                    activeInfoTab === 'privateNotes'
                      ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                      : 'border-transparent text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-border-subtle)]'
                  }`}
                >
                  Private Notes
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-4">
              {/* Notes Tab */}
              {activeInfoTab === 'notes' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300">
                      Notes
                    </label>
                    {defaultInvoiceNotes && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, notes: defaultInvoiceNotes }))}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                      >
                        Load default
                      </button>
                    )}
                  </div>
                  <MinimalRichTextEditor
                    value={formData.notes}
                    onChange={(html) => setFormData(prev => ({ ...prev, notes: html }))}
                    rows={6}
                    placeholder="Additional notes or comments..."
                    editorClassName="text-xs"
                  />
                </div>
              )}

              {/* Terms Tab */}
              {activeInfoTab === 'terms' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300">
                      Terms & Conditions
                    </label>
                    {defaultInvoiceTerms && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, terms: defaultInvoiceTerms }))}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                      >
                        Load default
                      </button>
                    )}
                  </div>
                  <MinimalRichTextEditor
                    value={formData.terms}
                    onChange={(html) => setFormData(prev => ({ ...prev, terms: html }))}
                    rows={6}
                    placeholder="Payment terms and conditions..."
                    editorClassName="text-xs"
                  />
                </div>
              )}

              {/* Private Notes Tab */}
              {activeInfoTab === 'privateNotes' && (
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Private Notes
                  </label>
                  <MinimalRichTextEditor
                    value={formData.privateNotes}
                    onChange={(html) => setFormData(prev => ({ ...prev, privateNotes: html }))}
                    rows={6}
                    placeholder="Private notes (not visible to clients)..."
                    editorClassName="text-xs"
                  />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    These notes are only visible to you and your team, not to clients.
                  </p>
                </div>
              )}
            </div>
            </CardContent>
          </Card>

            {/* Totals Summary - Right (30%) */}
            <Card>
            <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex justify-between text-body">
                  {/* VAT Rate */}                
                  <span className="text-xs text-[var(--ds-text-muted)] dark:text-[#99A1AF]">VAT Rate (%):</span>
                    <input
                      ref={vatRateInputRef}
                      type="text"
                      value={formData.vatRate}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string, numbers, and one decimal point
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          const numValue = value === '' ? 0 : parseFloat(value);
                          if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                            setFormData(prev => ({ ...prev, vatRate: numValue }));
                          } else if (value === '') {
                            setFormData(prev => ({ ...prev, vatRate: 0 }));
                          }
                        }
                      }}
                      className="w-auto px-2 py-1 bg-transparent border-0 focus:outline-none text-[var(--ds-text-primary)] text-right"
                    />
                </div>
              </div>

              <h2 className="text-h2 font-medium text-[var(--ds-text-primary)] mb-4">Invoice Totals</h2>
              <div className="space-y-2">
                <div className="flex justify-between text-body">
                  <span className="text-[var(--ds-text-muted)] dark:text-[#99A1AF]">Materials Subtotal:</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(totals.materialsSubtotal)}</span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-[var(--ds-text-muted)] dark:text-[#99A1AF]">Labour Subtotal:</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(totals.labourSubtotal)}</span>
                </div>
                <div className="flex justify-between text-body border-t border-[var(--ds-border-divider)] pt-2">
                  <span className="text-[var(--ds-text-muted)] dark:text-[#99A1AF]">Subtotal:</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(totals.subtotal)}</span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-[var(--ds-text-muted)] dark:text-[#99A1AF]">VAT ({formData.vatRate}%):</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(totals.vatAmount)}</span>
                </div>
                <div className="flex justify-between text-h2 font-bold border-t border-[var(--ds-border-divider)] pt-2">
                  <span className="text-[var(--ds-text-primary)]">Total:</span>
                  <span className="text-[var(--ds-text-primary)]">{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => handleNavigationAttempt(() => navigate('/tenant/invoices'))}
            >
              Cancel
            </Button>
            {isEditMode && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() =>
                    handleNavigationAttempt(() => navigate(`/tenant/invoices/${id}/send`))
                  }
                  disabled={saving}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={handlePrint}
                  disabled={saving}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() =>
                    handleNavigationAttempt(() => navigate(`/tenant/invoices/${id}/send`))
                  }
                  disabled={saving}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
              </>
            )}
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={saving}
            >
              {saving ? 'Saving...' : isEditMode ? 'Update Invoice' : 'Create Invoice'}
            </Button>
          </div>
        </form>
      </div>

      {/* Client Picker Modal */}
      {showClientPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Select Client</h2>
              <button
                onClick={() => setShowClientPicker(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <ClientPicker
              onSelect={handleClientSelect}
              onClose={() => setShowClientPicker(false)}
              showModal={false}
            />
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && isEditMode && id && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Log Payment</h2>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 text-2xl transition-colors"
              >
                ×
              </button>
            </div>
            
            <InvoicePaymentForm
              invoiceId={id}
              balanceDue={payableBalanceDue}
              onSuccess={async () => {
                setShowPaymentModal(false);
                // Refresh invoice data to get updated balance and status
                await fetchInvoice();
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 5000);
              }}
              onCancel={() => setShowPaymentModal(false)}
            />
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      <PaymentHistoryModal
        invoiceId={id}
        invoiceNum={formData.invoiceNum}
        open={showPaymentHistoryModal}
        onClose={() => setShowPaymentHistoryModal(false)}
      />

      {/* New Client Slide-Over */}
      <SlideOver
        open={showNewClientSlideOver}
        onOpenChange={setShowNewClientSlideOver}
        title="New Client"
        width="w-full max-w-2xl"
      >
        <NewClientForm
          onSuccess={(newClient) => {
            setSelectedClient(newClient);
            setFormData((prev) => {
              const next = { ...prev, clientId: newClient.id };
              if (prev.dateIssued) {
                const termsDays = getEffectiveInvoicePaymentTermsDays(newClient, defaultPaymentDueDays);
                next.dueDate = addDaysToIsoDate(prev.dateIssued, termsDays);
              }
              return next;
            });
            setShowNewClientSlideOver(false);
          }}
          onCancel={() => setShowNewClientSlideOver(false)}
        />
      </SlideOver>

      {/* Price Lookup Slide-Over */}
      <SlideOver
        open={showPriceLookupSlideOver}
        onOpenChange={setShowPriceLookupSlideOver}
        title="Price lookup"
        width="w-full max-w-2xl"
      >
        <div className="py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search
            </label>
            <Input
              type="text"
              placeholder="Search for materials..."
              className="h-8"
            />
          </div>
        </div>
      </SlideOver>

      {/* Labour Rate Calculations Info Slide-Over */}
      <SlideOver
        open={showLabourInfoSlideOver}
        onOpenChange={setShowLabourInfoSlideOver}
        title="Rate calculations"
        width="w-3/12"
      >
        <div className="py-4 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This is a quick reference guide for hourly calculations and conversion to decimals.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Hourly rate is converted to decimal by dividing the minutes by 60. For example, 15 minutes / 60 = 0.25. Below is a table for showing the decimal equivalent of the minutes at 5 minute intervals:
          </p>
          <table className="w-9/12 mx-auto border-collapse border border-gray-300 dark:border-gray-600">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <th className="text-center py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300">Minutes</th>
                <th className="text-center py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300">Decimal</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">5</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.08</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">10</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.17</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">15</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.25</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">20</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.33</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">25</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.42</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">30</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.5</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">35</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.58</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">40</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.67</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">45</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.75</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">50</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.83</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">55</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">0.92</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">60</td>
                <td className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 text-center">1</td>
              </tr>
            </tbody>
          </table>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Given the above table, if you enter 1 hour and 15 minutes in the hours field, the decimal will be 1.25.
          </p>
          
        </div>
      </SlideOver>

      {/* Certificate Link Modal */}
      <SlideOver
        open={showCertificateLinkModal}
        onOpenChange={setShowCertificateLinkModal}
        title="Link Certificates"
      >
        <div className="space-y-4">
          {!selectedClient ? (
            <p className="text-sm text-[var(--ds-text-secondary)]">
              Please select a client first to link certificates.
            </p>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-sm text-[var(--ds-text-secondary)] mb-4">
                  Select certificates to link to this invoice. Clients will be able to download linked certificates when the invoice status is "sent".
                </p>
                
                {/* Linked Certificates */}
                {linkedCertificates.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-[var(--ds-text-primary)] mb-2">Linked Certificates</h3>
                    <div className="space-y-2">
                      {linkedCertificates.map((cert) => (
                        <div
                          key={cert.id}
                          className="flex items-center justify-between p-3 bg-[var(--ds-control-field-bg)] rounded-md border border-[var(--ds-border-subtle)]"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-[var(--ds-text-muted)]" />
                              <span className="text-sm font-medium text-[var(--ds-text-primary)]">
                                {cert.certificateNum}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded bg-[var(--ds-control-field-bg)] text-[var(--ds-text-muted)] uppercase">
                                {cert.certificateType}
                              </span>
                            </div>
                            {cert.jobReference && (
                              <p className="text-xs text-[var(--ds-text-muted)] mt-1">
                                Job: {cert.jobReference}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                await invoicesAPI.unlinkCertificate(id, cert.id);
                                setLinkedCertificates(prev => prev.filter(c => c.id !== cert.id));
                              } catch (err) {
                                setError(err.message || 'Failed to unlink certificate');
                              }
                            }}
                            className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Certificates */}
                <div>
                  <h3 className="text-sm font-medium text-[var(--ds-text-primary)] mb-2">
                    Available Certificates
                  </h3>
                  {certificatesLoading ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--ds-accent-primary)]"></div>
                    </div>
                  ) : availableCertificates.length === 0 ? (
                    <p className="text-sm text-[var(--ds-text-muted)] py-4">
                      No certificates found for this client.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {availableCertificates.map((cert) => {
                        const isLinked = linkedCertificates.some(lc => lc.id === cert.id);
                        return (
                          <div
                            key={cert.id}
                            className={`flex items-center justify-between p-3 rounded-md border ${
                              isLinked
                                ? 'bg-[var(--ds-control-field-bg)] border-[var(--ds-border-subtle)] opacity-50'
                                : 'bg-white dark:bg-gray-700 border-[var(--ds-border-subtle)] hover:bg-[var(--ds-control-field-bg)]'
                            }`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-[var(--ds-text-muted)]" />
                                <span className="text-sm font-medium text-[var(--ds-text-primary)]">
                                  {cert.certificateNum}
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded bg-[var(--ds-control-field-bg)] text-[var(--ds-text-muted)] uppercase">
                                  {cert.certificateType}
                                </span>
                                {cert.status && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    cert.status === 'issued' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                                    cert.status === 'draft' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' :
                                    'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                  }`}>
                                    {cert.status}
                                  </span>
                                )}
                              </div>
                              {cert.jobReference && (
                                <p className="text-xs text-[var(--ds-text-muted)] mt-1">
                                  Job: {cert.jobReference}
                                </p>
                              )}
                            </div>
                            {isLinked ? (
                              <span className="text-xs text-[var(--ds-text-muted)]">Linked</span>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    setLinkingCertificate(true);
                                    await invoicesAPI.linkCertificate(id, cert.id);
                                    // Refresh linked certificates
                                    const updated = await invoicesAPI.getCertificates(id);
                                    setLinkedCertificates(updated);
                                    // Remove from available list
                                    setAvailableCertificates(prev => prev.filter(c => c.id !== cert.id));
                                  } catch (err) {
                                    setError(err.message || 'Failed to link certificate');
                                  } finally {
                                    setLinkingCertificate(false);
                                  }
                                }}
                                disabled={linkingCertificate}
                                className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                              >
                                <Link className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </SlideOver>
    </Layout>
  );
}

// New Client Form Component
const NewClientForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    client_type: 'individual',
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'GB',
    credit_limit: '',
    notes: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields based on client type
      if (formData.client_type === 'company' && !formData.company_name.trim()) {
        setError('Company name is required');
        setLoading(false);
        return;
      }
      
      if (formData.client_type === 'individual') {
        if (!formData.first_name.trim() || !formData.last_name.trim()) {
          setError('First name and last name are required');
          setLoading(false);
          return;
        }
      }

      const clientData = {
        ...formData,
        credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null,
      };

      const response = await clientsAPI.create(clientData);
      // Normalize client data to camelCase format (matching invoice API format)
      const normalizedClient = {
        id: response.client.id,
        clientType: response.client.client_type,
        companyName: response.client.company_name,
        firstName: response.client.first_name,
        lastName: response.client.last_name,
        email: response.client.email,
        phone: response.client.phone,
        mobile: response.client.mobile,
        addressLine1: response.client.address_line1,
        addressLine2: response.client.address_line2,
        city: response.client.city,
        county: response.client.county,
        postcode: response.client.postcode,
        country: response.client.country,
        creditLimit: response.client.credit_limit,
        currentBalance: response.client.current_balance,
        paymentTermsDays: response.client.payment_terms_days,
        notes: response.client.notes,
        isActive: response.client.is_active,
      };
      onSuccess(normalizedClient);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to create client');
      console.error('Create client error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Client Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Client Type <span className="text-red-500">*</span>
        </label>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="client_type"
              value="individual"
              checked={formData.client_type === 'individual'}
              onChange={handleChange}
              className="mr-2"
            />
            <span className="flex items-center gap-1 text-gray-900 dark:text-gray-100">
              <User className="h-4 w-4" /> Individual
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="client_type"
              value="company"
              checked={formData.client_type === 'company'}
              onChange={handleChange}
              className="mr-2"
            />
            <span className="flex items-center gap-1 text-gray-900 dark:text-gray-100">
              <Building2 className="h-4 w-4" /> Company
            </span>
          </label>
        </div>
      </div>

      {/* Company Fields */}
      {formData.client_type === 'company' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            required
            placeholder="Smith Electrical Ltd"
          />
        </div>
      )}

      {/* Individual Fields */}
      {formData.client_type === 'individual' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              placeholder="John"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              placeholder="Smith"
            />
          </div>
        </div>
      )}

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email
          </label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Phone
          </label>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="01234 567890"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Mobile
          </label>
          <Input
            type="tel"
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            placeholder="07700 900123"
          />
        </div>
      </div>

      {/* Address */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900 dark:text-gray-100">Address</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Address Line 1
          </label>
          <Input
            type="text"
            name="address_line1"
            value={formData.address_line1}
            onChange={handleChange}
            placeholder="123 Main Street"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Address Line 2
          </label>
          <Input
            type="text"
            name="address_line2"
            value={formData.address_line2}
            onChange={handleChange}
            placeholder="Apartment, suite, etc. (optional)"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              City
            </label>
            <Input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="London"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              County
            </label>
            <Input
              type="text"
              name="county"
              value={formData.county}
              onChange={handleChange}
              placeholder="Greater London"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Postcode
            </label>
            <Input
              type="text"
              name="postcode"
              value={formData.postcode}
              onChange={handleChange}
              placeholder="SW1A 1AA"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Country
            </label>
            <Input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              maxLength={2}
              placeholder="GB"
            />
          </div>
        </div>
      </div>

      {/* Financial */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Credit Limit (£)
        </label>
        <Input
          type="number"
          name="credit_limit"
          value={formData.credit_limit}
          onChange={handleChange}
          step="0.01"
          min="0"
          placeholder="0.00"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Notes
        </label>
        <Textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          placeholder="Additional notes about this client..."
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create Client'}
        </button>
      </div>
    </form>
  );
};

